<template>
  <div class="content-main">
    <!-- Modal: Buscar documento Infoauto -->
    <BaseModal v-model="showSearchModal" class="search-modal-wrap">
      <div class="infoauto-search">
        <h3>Buscar documento en Infoauto</h3>

        <!-- Paso 1: Filtros + resultados -->
        <div v-if="searchStep === 1">
          <div class="search-filters">
            <div class="filter-row">
              <label>Tipo</label>
              <select v-model="searchFilters.tipo">
                <option value="FAC">Factura (FAC)</option>
                <option value="N/E">Nota de Entrega (N/E)</option>
              </select>
            </div>
            <div class="filter-row">
              <label>Departamento</label>
              <select v-model="searchFilters.departmentCode">
                <option
                  v-for="dept in departments"
                  :key="dept.codigo"
                  :value="dept.codigo"
                  >{{ dept.nombre }}</option
                >
              </select>
            </div>
            <div class="filter-row">
              <label>Desde</label>
              <input type="date" v-model="searchFilters.fechaIni" />
            </div>
            <div class="filter-row">
              <label>Hasta</label>
              <input type="date" v-model="searchFilters.fechaFin" />
            </div>
            <div class="filter-row">
              <label>Nro. Documento</label>
              <input
                type="text"
                v-model="searchFilters.numeroDoc"
                placeholder="Ej: 52051"
              />
            </div>
            <div class="filter-row cliente-search-wrap">
              <label>Cliente</label>
              <div class="cliente-input-group">
                <div class="cliente-selected" v-if="searchFilters.clienteId">
                  <span>{{ searchFilters.clienteName }}</span>
                  <button
                    class="btn-clear"
                    @click="clearCliente"
                    title="Quitar filtro"
                  >
                    &times;
                  </button>
                </div>
                <template v-else>
                  <input
                    type="text"
                    v-model="clienteSearch"
                    @input="onClienteInput"
                    placeholder="Buscar cliente..."
                    autocomplete="off"
                  />
                  <div
                    class="cliente-dropdown"
                    v-if="clienteResults.length > 0 || clienteSearchLoading"
                  >
                    <div
                      v-if="clienteSearchLoading"
                      class="cliente-dropdown__item cliente-dropdown__loading"
                    >
                      Buscando...
                    </div>
                    <div
                      v-for="c in clienteResults"
                      :key="c.personaId"
                      class="cliente-dropdown__item"
                      @mousedown.prevent="selectCliente(c)"
                    >
                      <strong>{{ c.nombreComp }}</strong>
                      <span class="cliente-id">{{ c.personaId }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <button
              class="btn-primary"
              @click="searchDocuments"
              :disabled="searchLoading"
            >
              {{ searchLoading ? "Buscando..." : "Buscar" }}
            </button>
          </div>

          <div v-if="searchError" class="error-msg">{{ searchError }}</div>

          <div v-if="searchResults.length > 0" class="search-results">
            <p>{{ searchResults.length }} documento(s) encontrado(s)</p>
            <table class="default-table">
              <thead>
                <tr>
                  <th>Nro. Doc</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>Mon.</th>
                  <th>Líneas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="doc in searchResults"
                  :key="doc.documentoId"
                  :class="{
                    'row-selected':
                      selectedDocument &&
                      selectedDocument.documentoId === doc.documentoId
                  }"
                  @click="previewDocument(doc)"
                  class="clickable-row"
                >
                  <td>{{ doc.numeroDoc }}</td>
                  <td>{{ doc.tipoDocumento }}</td>
                  <td>{{ doc.nomCliente }}</td>
                  <td>{{ doc.fEmision }}</td>
                  <td>{{ doc.total }}</td>
                  <td>{{ doc.moneda }}</td>
                  <td>
                    <span class="lines-badge">{{
                      doc.detalle ? doc.detalle.length : 0
                    }}</span>
                  </td>
                  <td>
                    <button
                      class="btn-small btn-primary"
                      @click.stop="selectDocument(doc)"
                    >
                      Seleccionar
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else-if="searchDone && !searchLoading" class="no-results">
            No se encontraron documentos.
          </div>

          <!-- Preview inline del documento seleccionado -->
          <div v-if="selectedDocument" class="doc-preview">
            <div class="doc-preview__header">
              <span>
                <strong
                  >{{ selectedDocument.tipoDocumento }} #{{
                    selectedDocument.numeroDoc
                  }}</strong
                >
                &mdash; {{ selectedDocument.nomCliente }} &mdash;
                {{ selectedDocument.fEmision }} &mdash;
                {{ selectedDocument.total }} {{ selectedDocument.moneda }}
              </span>
              <button class="btn-primary btn-small" @click="searchStep = 2">
                Crear Recepción &rarr;
              </button>
            </div>
            <table class="default-table preview-table">
              <thead>
                <tr>
                  <th>Código NKR</th>
                  <th>Código Int.</th>
                  <th>Cantidad</th>
                  <th>Precio</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in selectedDocument.detalle" :key="i">
                  <td>{{ item.codigo }}</td>
                  <td>{{ item.codigoInt }}</td>
                  <td>
                    <strong>{{ item.cant }}</strong>
                  </td>
                  <td>{{ item.precio }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Paso 2: Confirmar creación -->
        <div v-if="searchStep === 2 && selectedDocument">
          <div class="confirm-layout">
            <!-- Columna izquierda: formulario -->
            <div class="confirm-form">
              <h4>Datos de recepción</h4>
              <div class="filter-row">
                <label>Almacén <span class="required">*</span></label>
                <select
                  v-model="createForm.warehouseId"
                  @change="onWarehouseChange"
                >
                  <option value="">-- Seleccionar --</option>
                  <option
                    v-for="dep in depositos"
                    :key="dep.depositoId"
                    :value="dep.depositoId"
                    >{{ dep.codDeposito }} —
                    {{ dep.descDeposito || dep.depositoId }}</option
                  >
                </select>
              </div>
              <div class="filter-row">
                <label>Departamento <span class="required">*</span></label>
                <select v-model="createForm.departamentoId">
                  <option
                    v-for="dept in departments"
                    :key="dept.departamentoId"
                    :value="dept.departamentoId"
                    >{{ dept.nombre }}</option
                  >
                </select>
              </div>
              <div class="filter-row">
                <label>Notas</label>
                <input
                  type="text"
                  v-model="createForm.notes"
                  placeholder="Opcional"
                />
              </div>
              <div class="action-buttons">
                <button class="btn-secondary" @click="searchStep = 1">
                  Volver
                </button>
                <button
                  class="btn-primary"
                  @click="createReceptionFromInfoauto"
                  :disabled="
                    createLoading ||
                      !createForm.warehouseId ||
                      !createForm.departamentoId
                  "
                >
                  {{ createLoading ? "Creando..." : "Confirmar y Crear" }}
                </button>
              </div>
              <div v-if="createError" class="error-msg">{{ createError }}</div>
            </div>

            <!-- Columna derecha: preview del documento -->
            <div class="confirm-preview">
              <h4>
                {{ selectedDocument.tipoDocumento }} #{{
                  selectedDocument.numeroDoc
                }}
                <span class="preview-meta">{{
                  selectedDocument.nomCliente
                }}</span>
              </h4>
              <p class="preview-totals">
                Fecha: {{ selectedDocument.fEmision }} &nbsp;|&nbsp; Total:
                {{ selectedDocument.total }}
                {{ selectedDocument.moneda }} &nbsp;|&nbsp;
                {{
                  selectedDocument.detalle ? selectedDocument.detalle.length : 0
                }}
                línea(s)
              </p>
              <table class="default-table preview-table">
                <thead>
                  <tr>
                    <th>Código NKR</th>
                    <th>Código Int.</th>
                    <th>Cantidad</th>
                    <th>Precio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in selectedDocument.detalle" :key="i">
                    <td>{{ item.codigo }}</td>
                    <td>{{ item.codigoInt }}</td>
                    <td>
                      <strong>{{ item.cant }}</strong>
                    </td>
                    <td>{{ item.precio }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>

    <!-- Contenido principal -->
    <div class="content">
      <div class="header">
        <span>Recepciones</span>
        <button class="btn-primary" @click="openSearchModal">
          + Nueva desde Infoauto
        </button>
      </div>

      <!-- Filtros de la lista -->
      <div class="list-filters">
        <div class="status-tabs">
          <button
            v-for="tab in statusTabs"
            :key="tab.value"
            :class="['tab-btn', { active: listFilters.status === tab.value }]"
            @click="setStatusFilter(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="doc-number-filter">
          <input
            type="text"
            v-model="listFilters.documentNumber"
            placeholder="Buscar por nro. documento..."
          />
        </div>
      </div>

      <div class="table-wrap">
        <div v-if="loading" class="loading">Cargando...</div>
        <table class="default-table receptions-table" v-else>
          <thead>
            <tr>
              <th>#</th>
              <th>Doc. Infoauto</th>
              <th>Nro. Documento</th>
              <th>Proveedor</th>
              <th>Almacén</th>
              <th>Departamento</th>
              <th>Items</th>
              <th>Estado</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="rec in receptions"
              :key="rec.id"
              @click="openDetail(rec.id)"
              class="clickable-row"
            >
              <td>{{ rec.id }}</td>
              <td>{{ rec.infoautoDocumentoId || "-" }}</td>
              <td>
                <strong>{{ rec.documentNumber || "-" }}</strong>
              </td>
              <td>{{ rec.supplier || "-" }}</td>
              <td>{{ rec.warehouseCode || rec.warehouseId }}</td>
              <td>{{ rec.departamentoId }}</td>
              <td>
                <span class="items-count" v-if="rec.items">{{
                  rec.items.length
                }}</span>
                <span class="items-count" v-else>-</span>
              </td>
              <td>
                <span :class="['status-badge', `status-${rec.status}`]">{{
                  statusLabel(rec.status)
                }}</span>
              </td>
              <td>{{ rec.createdAt ? rec.createdAt.substring(0, 10) : "" }}</td>
            </tr>
            <tr v-if="receptions.length === 0">
              <td colspan="9" class="no-data">
                No hay recepciones registradas.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import BaseModal from "@/components/global/_base-modal.vue";
import {
  getReceptions,
  searchInfoautoDocuments,
  createFromInfoauto
} from "@/api/ReceptionsService.js";
import { getDepositos, searchPersonInfoauto } from "@/api/Infoauto.js";

export default {
  name: "ReceptionsPage",
  components: { BaseModal },
  data() {
    return {
      receptions: [],
      loading: true,
      showSearchModal: false,
      searchStep: 1,
      searchFilters: {
        tipo: "FAC",
        departmentCode: "",
        fechaIni: (() => {
          const d = new Date();
          d.setFullYear(d.getFullYear() - 1);
          return d.toISOString().substring(0, 10);
        })(),
        fechaFin: new Date().toISOString().substring(0, 10),
        numeroDoc: "",
        clienteId: "",
        clienteName: ""
      },
      clienteSearch: "",
      clienteResults: [],
      clienteSearchLoading: false,
      clienteSearchTimeout: null,
      searchResults: [],
      searchLoading: false,
      searchDone: false,
      searchError: null,
      selectedDocument: null,
      depositos: [],
      createForm: {
        warehouseId: "",
        warehouseCode: "",
        departamentoId: "",
        notes: ""
      },
      createLoading: false,
      createError: null,
      listFilters: {
        status: "",
        documentNumber: ""
      },
      listFilterTimeout: null
    };
  },
  computed: {
    departments() {
      return this.$store.getters["general/getDepartments"] || [];
    },
    statusTabs() {
      return [
        { label: "Todos", value: "" },
        { label: "Pendiente", value: "pending" },
        { label: "Verificada", value: "verified" },
        { label: "Confirmada", value: "confirmed" },
        { label: "Completada", value: "completed" }
      ];
    }
  },
  watch: {
    "listFilters.documentNumber"(val) {
      clearTimeout(this.listFilterTimeout);
      this.listFilterTimeout = setTimeout(() => {
        this.loadReceptions();
      }, 400);
    }
  },
  mounted() {
    this.loadReceptions();
    this.loadDepositos();
  },
  methods: {
    async loadDepositos() {
      try {
        const res = await getDepositos();
        this.depositos = Array.isArray(res) ? res : [];
      } catch (e) {
        // non-critical, user can retry
      }
    },
    async loadReceptions() {
      this.loading = true;
      try {
        const res = await getReceptions({
          status: this.listFilters.status || undefined,
          documentNumber: this.listFilters.documentNumber || undefined
        });
        this.receptions = Array.isArray(res) ? res : [];
      } catch (e) {
        alert("Error cargando recepciones");
      } finally {
        this.loading = false;
      }
    },
    setStatusFilter(status) {
      this.listFilters.status = status;
      this.loadReceptions();
    },
    openSearchModal() {
      this.showSearchModal = true;
      this.searchStep = 1;
      this.searchResults = [];
      this.searchDone = false;
      this.searchError = null;
      this.selectedDocument = null;
      this.createError = null;
      if (this.departments.length > 0) {
        this.searchFilters.departmentCode = this.departments[0].codigo;
        this.createForm.departamentoId = this.departments[0].departamentoId;
      }
    },
    async searchDocuments() {
      this.searchLoading = true;
      this.searchError = null;
      this.searchDone = false;
      try {
        const res = await searchInfoautoDocuments(this.searchFilters);
        // Infoauto returns an error array like [{status:"406", message:"..."}] on failure
        if (Array.isArray(res) && res.length > 0 && res[0].message) {
          this.searchError = res[0].message;
          this.searchResults = [];
        } else {
          this.searchResults =
            res && res.data ? res.data : Array.isArray(res) ? res : [];
        }
        this.searchDone = true;
      } catch (e) {
        this.searchError = "Error consultando Infoauto. Verifica los filtros.";
      } finally {
        this.searchLoading = false;
      }
    },
    onClienteInput() {
      clearTimeout(this.clienteSearchTimeout);
      if (!this.clienteSearch || this.clienteSearch.length < 2) {
        this.clienteResults = [];
        return;
      }
      this.clienteSearchLoading = true;
      this.clienteSearchTimeout = setTimeout(async () => {
        try {
          const res = await searchPersonInfoauto(this.clienteSearch);
          this.clienteResults = res && res.data ? res.data : [];
        } catch (e) {
          this.clienteResults = [];
        } finally {
          this.clienteSearchLoading = false;
        }
      }, 350);
    },
    selectCliente(c) {
      this.searchFilters.clienteId = c.personaId;
      this.searchFilters.clienteName = c.nombreComp;
      this.clienteSearch = "";
      this.clienteResults = [];
    },
    clearCliente() {
      this.searchFilters.clienteId = "";
      this.searchFilters.clienteName = "";
      this.clienteSearch = "";
      this.clienteResults = [];
    },
    onWarehouseChange() {
      const dep = this.depositos.find(
        d => d.depositoId === this.createForm.warehouseId
      );
      this.createForm.warehouseCode = dep ? dep.codDeposito : "";
    },
    previewDocument(doc) {
      this.selectedDocument = doc;
      this.createError = null;
      const dept = this.departments.find(
        d => d.codigo === this.searchFilters.departmentCode
      );
      if (dept) {
        this.createForm.departamentoId = dept.departamentoId;
      }
    },
    selectDocument(doc) {
      this.selectedDocument = doc;
      this.searchStep = 2;
      this.createError = null;
      const dept = this.departments.find(
        d => d.codigo === this.searchFilters.departmentCode
      );
      if (dept) {
        this.createForm.departamentoId = dept.departamentoId;
      }
    },
    async createReceptionFromInfoauto() {
      this.createLoading = true;
      this.createError = null;
      try {
        const reception = await createFromInfoauto({
          documentoId: this.selectedDocument.documentoId,
          departamentoId: this.createForm.departamentoId,
          departmentCode: this.searchFilters.departmentCode,
          warehouseId: this.createForm.warehouseId,
          warehouseCode: this.createForm.warehouseCode,
          tipo: this.searchFilters.tipo,
          notes: this.createForm.notes
        });
        this.showSearchModal = false;
        this.$router.push(`/receptions/${reception.id}`);
      } catch (e) {
        this.createError = "Error creando la recepción.";
      } finally {
        this.createLoading = false;
      }
    },
    statusLabel(status) {
      const labels = {
        pending: "Pendiente",
        verified: "Verificada",
        confirmed: "Confirmada",
        draft: "Borrador",
        completed: "Completada",
        partial: "Parcial",
        cancelled: "Cancelada"
      };
      return labels[status] || status;
    },
    openDetail(id) {
      this.$router.push(`/receptions/${id}`);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.content-main {
  width: 100%;
  height: 100%;

  .content {
    padding: 5px;
    width: 100%;

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 15px;
      font-size: 20px;
      font-weight: 600;
    }

    .list-filters {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 0 15px 12px;
      flex-wrap: wrap;

      .status-tabs {
        display: flex;
        gap: 4px;

        .tab-btn {
          padding: 5px 14px;
          border: 1px solid #ddd;
          border-radius: 20px;
          background: white;
          cursor: pointer;
          font-size: 13px;
          color: #555;
          transition: all 0.15s;

          &:hover {
            border-color: #e63946;
            color: #e63946;
          }
          &.active {
            background: #e63946;
            color: white;
            border-color: #e63946;
          }
        }
      }

      .doc-number-filter input {
        padding: 5px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 13px;
        min-width: 200px;
        &:focus {
          outline: none;
          border-color: #e63946;
        }
      }
    }

    .table-wrap {
      padding: 0 15px;
      overflow-x: auto;
    }
  }
}

.clickable-row {
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
    color: #000;
  }
}

.no-data {
  text-align: center;
  color: #999;
  padding: 20px;
}

.status-badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  &.status-pending {
    background: #fff3e0;
    color: #e65100;
  }
  &.status-verified {
    background: #e3f2fd;
    color: #1565c0;
  }
  &.status-confirmed {
    background: #e8f5e9;
    color: #2e7d32;
  }
  &.status-draft {
    background: #e0e0e0;
    color: #555;
  }
  &.status-completed {
    background: #c8f7c5;
    color: #2e7d32;
  }
  &.status-partial {
    background: #ffe0b2;
    color: #e65100;
  }
  &.status-cancelled {
    background: #ffcdd2;
    color: #c62828;
  }
}

.table-wrap {
  padding: 0 15px;
  overflow-x: auto;
}

.receptions-table {
  td,
  th {
    white-space: nowrap;
  }
  .items-count {
    display: inline-block;
    background: #e8eaf6;
    color: #3949ab;
    border-radius: 10px;
    padding: 1px 8px;
    font-size: 12px;
    font-weight: 600;
  }
}

.infoauto-search {
  h3 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
  }
  h4 {
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 600;
  }

  .search-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-end;
    margin-bottom: 16px;
  }

  .filter-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    label {
      font-size: 12px;
      color: #555;
    }
    input,
    select {
      padding: 6px 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
      min-width: 140px;
    }
    .required {
      color: red;
    }
  }

  .cliente-search-wrap {
    position: relative;
    min-width: 200px;
  }

  .cliente-input-group {
    position: relative;

    input {
      width: 100%;
    }

    .cliente-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 100;
      background: white;
      border: 1px solid #ccc;
      border-top: none;
      border-radius: 0 0 4px 4px;
      max-height: 200px;
      overflow-y: auto;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

      &__item {
        padding: 7px 10px;
        font-size: 13px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        &:hover {
          background: #f5f5f5;
        }
        .cliente-id {
          color: #999;
          font-size: 11px;
          white-space: nowrap;
        }
      }
      &__loading {
        color: #999;
        cursor: default;
        &:hover {
          background: none;
        }
      }
    }
  }

  .cliente-selected {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    background: #e8f5e9;
    border: 1px solid #a5d6a7;
    border-radius: 4px;
    font-size: 13px;
    min-width: 140px;

    span {
      flex: 1;
    }
    .btn-clear {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      color: #666;
      padding: 0 2px;
      line-height: 1;
      &:hover {
        color: #c62828;
      }
    }
  }

  .search-results {
    margin-top: 16px;
    overflow-x: auto;
    p {
      font-size: 13px;
      color: #666;
      margin-bottom: 8px;
    }
  }

  .row-selected {
    background-color: #fff3e0 !important;
  }

  .lines-badge {
    display: inline-block;
    background: #e8eaf6;
    color: #3949ab;
    border-radius: 10px;
    padding: 1px 8px;
    font-size: 12px;
    font-weight: 600;
  }

  .doc-preview {
    margin-top: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 14px;
      background: #f5f5f5;
      font-size: 13px;
    }
  }

  .preview-table {
    font-size: 13px;
    td,
    th {
      padding: 5px 10px;
      white-space: nowrap;
    }
  }

  .no-results {
    margin-top: 16px;
    color: #999;
    font-size: 14px;
  }

  // Paso 2: layout 2 columnas
  .confirm-layout {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 24px;
    align-items: start;
  }

  .confirm-form {
    .filter-row {
      margin-bottom: 10px;
      input,
      select {
        width: 100%;
      }
    }
    .action-buttons {
      display: flex;
      gap: 10px;
      margin-top: 16px;
    }
  }

  .confirm-preview {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px;
    overflow-x: auto;

    .preview-meta {
      font-weight: 400;
      font-size: 13px;
      color: #666;
      margin-left: 8px;
    }
    .preview-totals {
      font-size: 12px;
      color: #666;
      margin-bottom: 10px;
    }
  }

  .error-msg {
    color: red;
    margin-top: 8px;
    font-size: 13px;
  }
}

.btn-primary {
  background-color: #e63946;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background-color: #c1121f;
  }
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background-color: #565e64;
  }
}

.btn-small {
  padding: 4px 10px;
  font-size: 12px;
}

.loading {
  padding: 20px;
  color: #999;
}
</style>

<!-- Override ancho del modal sin scoped para afectar el componente hijo -->
<style lang="scss">
.search-modal-wrap .modal-content {
  width: calc(100vw - 120px);
  max-width: 1400px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
}
</style>
